import { DataType } from "../../../tables"
import { useFiltersHooks } from "../../../features/ui"
import { normalize } from "../../../utils/typeUtils"
import { Budget } from "../state/types"
import { useCustomerLoaders } from "../loaders/customerLoaders"
import { isSelectablePaymentType, useAdministrationHooks, useEmployeeLoaders, usePaymentTypeLoaders } from "../../administration"
import { BudgetPriceItem } from "./types"
import { CreditAccountPaymentTypeId } from "../../../features/data-types"
import { useBudgetLoaders } from "../loaders/budgetLoaders"
import { Params } from "../../../state/reducers/types"
import { ListResponse } from "../../common"
import { lastItem } from "../../../utils/listUtils"

export const useBudgetHooks = () => {
    const { applyPaymentTypeAdjustment } = useAdministrationHooks()
    const { search2 } = useFiltersHooks()
    
    const budgetLoaders = useBudgetLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const employeeLoaders = useEmployeeLoaders()
    const customerLoaders = useCustomerLoaders()
    
    const getBudgetPricesList = (basePrice: number): BudgetPriceItem[] => {
        const list: BudgetPriceItem[] = []
        const { findAllPaymentTypes } = paymentTypeLoaders()
        findAllPaymentTypes(true)
            .filter(paymentType => paymentType.id !== CreditAccountPaymentTypeId && isSelectablePaymentType(paymentType))
            .forEach(paymentType => {
                list.push({
                    paymentType: paymentType,
                    price: applyPaymentTypeAdjustment(basePrice, paymentType)
                })
            })
        return list.sort((a, b) => a.price - b.price)
    }

    const loadFiltered = async (
        searchText = '',
        sellerId?: string,
        customerId?: string,
        dateFrom?: Date,
        dateTo?: Date,
        dbRequest = false
    ) => {
        const { loadAllBudgets } = budgetLoaders()
        const budgets = await loadAllBudgets(true)
        
        const { loadCustomers } = customerLoaders()
        const customers = await loadCustomers(budgets.map(budget => budget.customerId))
        const valuesFn = async (budgetDT: DataType) => {
            const budget = budgetDT as Budget
            const { findEmployee } = employeeLoaders()
            const seller = findEmployee(budget.sellerId)!
            const customer = customers.find(customer => customer.id === budget.customerId)!
            return [
                budget.code.toString(),
                normalize(seller.name),
                normalize(customer!.name)
            ]
        }

        const filterFn = (budgetDT: DataType) => {
            const budget = budgetDT as Budget
            return (!sellerId || budget.sellerId === sellerId) &&
                (!customerId || budget.customerId === customerId) &&
                (!dateFrom || new Date(budget.date) >= dateFrom) &&
                (!dateTo || new Date(budget.date) <= dateTo)
        }

        const sortFn = (budgetDT_A: DataType, budgetDT_B: DataType) => 
            (budgetDT_B as Budget).code > (budgetDT_A as Budget).code ? 1 : -1

        return search2(budgets, searchText, valuesFn, filterFn, sortFn) as Promise<Budget[]>
    }

    const loadFilteredBatch = async (
        searchText = '',
        sellerId?: string,
        customerId?: string,
        dateFrom?: Date,
        dateTo?: Date,
        startKey?: Params,
        limit = 10,
        dbRequest = false
    ): Promise<ListResponse<Budget>> => {
        const budgets = await loadFiltered(searchText, sellerId, customerId, dateFrom, dateTo, dbRequest)

        const index = startKey ? budgets.map(budget => budget.id).indexOf(startKey!.id as string) + 1 : 0
        const batch = budgets.slice(index, index + limit)
        const lastKey = lastItem(batch)?.id
        
        return {
            dataTypes: batch,
            lastKey: lastKey ? { id: lastKey } : undefined
        }
    }

    const countFiltered = async (
        searchText = '',
        sellerId?: string,
        customerId?: string,
        dateFrom?: Date,
        dateTo?: Date,
        startKey?: Params
    ): Promise<number> => {
        const budgets = await loadFiltered(searchText, sellerId, customerId, dateFrom, dateTo)
        const index = startKey ? budgets.map(budget => budget.id).indexOf(startKey!.id as string) + 1 : 0
        const nextBatch = budgets.slice(index)
        return nextBatch.length
    }

    return {
        getBudgetPricesList,
        loadFilteredBatch,
        countFiltered
    }
}
